/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const Maintenances = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { setExportId, isExporting } = useReport()

  const { selectedOrgan } = useOrgan()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const techniciansSelect = useRemoteSelectOptions({
    queryKey: ['technicians'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        'roles.name': ['Técnico'],
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }
      const response = await baseServiceGet(entities.users.get, params)
      return response.result
    },
    fieldId: 'filter.technician_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
  })

  const typeSelect = useRemoteSelectOptions({
    queryKey: ['types'],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: 'filter.type_id',
    formData,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const maintenanceTypesSelect = useRemoteSelectOptions({
    queryKey: ['maintenanceTypes'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.maintenanceTypes.get,
        params
      )
      return response.result
    },
    fieldId: 'filter.maintenance_type_id',
    formData,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const technicianStatusSelect = useRemoteSelectOptions({
    queryKey: ['technicianStatus'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.technicianStatus.get,
        params
      )
      return response.result
    },
    fieldId: 'filter.technician_status_id',
    formData,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const equipmentsSelect = useRemoteSelectOptions({
    queryKey: ['equipments'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        is_completed: true,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.equipments.get, params)
      return response.result
    },
    fieldId: 'filter.equipment_id',
    formData,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const selects = {
    'filter.technician_id': techniciansSelect,
    'filter.type_id': typeSelect,
    'filter.maintenance_type_id': maintenanceTypesSelect,
    'filter.technician_status_id': technicianStatusSelect,
    'filter.equipment_id': equipmentsSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const exportReport = useMutation({
    mutationFn: async (type) => {
      const params = {
        filters: {
          ...(formData.filter.start_date && {
            start_date: formData.filter.start_date,
          }),
          ...(formData.filter.end_date && {
            end_date: formData.filter.end_date,
          }),
          ...(formData.filter.technician_id?.value && {
            technician_id: formData.filter.technician_id?.value,
          }),
          ...(formData.filter.maintenance_type_id?.value && {
            maintenance_type_id: formData.filter.maintenance_type_id?.value,
          }),
          ...(formData.filter.technician_status_id?.value && {
            technician_status_id: formData.filter.technician_status_id?.value,
          }),
          ...(formData.filter.equipment_id?.value && {
            equipment_id: formData.filter.equipment_id?.value,
          }),
          organization_id: selectedOrgan.id,
        },
      }
      const response = await baseServiceGet(
        entities.reports.maintenances.get,
        params,
        {
          type,
        }
      )
      return response
    },
    onMutate: () => {
      showLoader()
    },

    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error(error)
    },

    onSettled: () => {
      hideLoader()
    },
  })

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Manutenções" showBackButton={false} />
      <Alert
        severity="info"
        sx={{
          mb: 2,
        }}>
        <strong>Atenção:</strong> Esse relatório mostra todas as manutenções
        realizadas.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default Maintenances
